import { SlidersHorizontal, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { cn } from '@/utils/cn';

/** Temporarily hidden — restore when filter summary copy is redesigned. */
const SHOW_APPLIED_COUNT_LABEL = false;

/**
 * The filter row above a list.
 *
 * Search and filters share one row on desktop. On small screens the filters
 * collapse behind a single control so six pickers do not crowd out the list.
 * The applied count is on the collapsed trigger so a hidden filter can never
 * quietly explain why a list looks empty.
 */

export interface FilterBarProps {
  /**
   * The filter controls. Given as a function so the bar can hand each control the
   * portal target it was built with — a filter bar inside a dialog whose pickers
   * portal to the body is the bug this shape prevents.
   */
  children: ReactNode | ((props: { popoverPortal: PopoverPortalTarget }) => ReactNode);
  /** How many filters are currently narrowing the list. */
  appliedCount: number;
  onClearAll: () => void;
  labels: {
    filters: string;
    clearAll: string;
    applied: (count: number) => string;
  };
  /** Search usually sits outside the collapse, since it is used most. */
  leading?: ReactNode;
  trailing?: ReactNode;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
}

export const FilterBar = ({
  children,
  appliedCount,
  onClearAll,
  labels,
  leading,
  trailing,
  popoverPortal = 'body',
  className,
}: FilterBarProps) => {
  const [expanded, setExpanded] = useState(false);
  const filters =
    typeof children === 'function' ? children({ popoverPortal }) : children;

  return (
    <div className={cn('mb-4 flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-end gap-2">
        {leading ? <div className="w-full min-w-0 md:w-auto">{leading}</div> : null}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setExpanded((current) => !current)}
          leadingIcon={<SlidersHorizontal className="h-4 w-4" />}
          className="md:hidden"
          aria-expanded={expanded}
        >
          {labels.filters}
          {appliedCount > 0 ? (
            <Badge variant="brand" className="ml-1.5">
              {appliedCount}
            </Badge>
          ) : null}
        </Button>

        <div
          className={cn(
            'w-full flex-wrap items-end gap-2',
            expanded ? 'flex' : 'hidden',
            'md:contents',
          )}
        >
          {filters}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {appliedCount > 0 ? (
            <>
              {SHOW_APPLIED_COUNT_LABEL ? (
                <span className="hidden text-body-sm text-fg-secondary sm:inline">
                  {labels.applied(appliedCount)}
                </span>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                leadingIcon={<X className="h-3.5 w-3.5" />}
              >
                {labels.clearAll}
              </Button>
            </>
          ) : null}
          {trailing}
        </div>
      </div>
    </div>
  );
};
